// External / offline supplier picker for the maintenance & task editor
// (assignee_type === "external"). Extracted from components.jsx (P3.c
// follow-up) to keep that file under the size guideline.

import { Input } from "../../../components/ui/input";
import { SUPPLIER_CATEGORY_LABELS, SUPPLIER_CATEGORY_KEYS, labelForSupplierCategory } from "../../../lib/supplierCategories";

/**
 * Picker for offline / external suppliers — used in the maintenance & task
 * editor when assignee_type === "external". Mirrors the supplier picker in
 * Invoices.jsx (single source of truth would be ideal but the form-state
 * shape is different here — keep them in sync manually).
 *
 * Supports three modes via the dropdown:
 *   ""        — no selection yet
 *   "__new__" — user wants to add a brand-new supplier inline
 *   <id>      — existing supplier picked
 *
 * "+ Add new" reveals a Name input + a Yes/No "save to your network?"
 * toggle. Yes opens the contact-detail mini-form so the supplier is
 * persisted to /api/suppliers and assigned to the active yacht on save.
 */
export function ExternalSupplierPicker({ form, setForm, suppliers, providerMembers = [] }) {
  // Online suppliers = provider-role crew (they hold a Seavanto account).
  const online = (providerMembers || [])
    .filter((m) => m && (m.user_id || m.id))
    .slice()
    .sort((a, b) => (a.company || a.name || a.email || "").localeCompare(b.company || b.name || b.email || ""));
  // Offline suppliers = address-book vendors with NO Seavanto account.
  // Exclude linked (online) suppliers — they already appear in the "Online
  // suppliers" group above via providerMembers, so without this filter a
  // linked supplier showed up in BOTH lists (user-reported duplicate).
  const offline = (suppliers || [])
    .filter((s) => s && s.id && !s.user_id)
    .slice()
    .sort((a, b) => (a.name || "").localeCompare(b.name || ""));

  const onChange = (val) => {
    if (val === "__new__") {
      setForm({
        ...form,
        assigned_to: "",
        external_supplier_id: "",
        external_supplier_name: "",
        _new_supplier: true,
        _new_supplier_save: null,
        _new_supplier_type: "company",
        _new_supplier_category: "General / Other",
        _new_supplier_contact_person: "",
        _new_supplier_email: "",
        _new_supplier_phone: "",
      });
      return;
    }
    if (val.startsWith("prov:")) {
      const uid = val.slice(5);
      const m = online.find((x) => (x.user_id || x.id) === uid);
      setForm({
        ...form,
        assigned_to: uid,
        external_supplier_id: "",
        external_supplier_name: m ? (m.company || m.name || m.email || "") : "",
        _new_supplier: false,
        _new_supplier_save: null,
      });
      return;
    }
    if (val.startsWith("sup:")) {
      const sid = val.slice(4);
      const sup = offline.find((s) => s.id === sid);
      setForm({
        ...form,
        assigned_to: "",
        external_supplier_id: sid,
        external_supplier_name: sup ? sup.name : "",
        _new_supplier: false,
        _new_supplier_save: null,
      });
      return;
    }
    // cleared
    setForm({
      ...form,
      assigned_to: "",
      external_supplier_id: "",
      external_supplier_name: "",
      _new_supplier: false,
      _new_supplier_save: null,
    });
  };

  const pickerValue = form._new_supplier
    ? "__new__"
    : form.assigned_to
      ? `prov:${form.assigned_to}`
      : form.external_supplier_id
        ? `sup:${form.external_supplier_id}`
        : "";

  return (
    <div className="space-y-2">
      <select
        className="h-9 w-full rounded-md border border-input px-3 text-sm"
        value={pickerValue}
        onChange={(e) => onChange(e.target.value)}
        data-testid="maint-external-supplier-picker"
      >
        <option value="">— pick a supplier —</option>
        {online.length > 0 && (
          <optgroup label="Online suppliers">
            {online.map((m) => (
              <option key={m.user_id || m.id} value={`prov:${m.user_id || m.id}`}>
                {m.company || m.name || m.email || "—"}{m.company && m.name ? ` · ${m.name}` : ""}
              </option>
            ))}
          </optgroup>
        )}
        {offline.length > 0 && (
          <optgroup label="Offline suppliers">
            {offline.map((s) => (
              <option key={s.id} value={`sup:${s.id}`}>
                {s.name}{s.category ? ` · ${labelForSupplierCategory(s.category)}` : ""}
              </option>
            ))}
          </optgroup>
        )}
        <option value="__new__">+ Add new supplier</option>
      </select>

      {form._new_supplier && (
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3 space-y-3" data-testid="maint-new-supplier-box">
          <div>
            <div className="text-xs font-medium text-zinc-700 mb-1">Supplier name *</div>
            <Input
              value={form.external_supplier_name}
              onChange={(e) => setForm({ ...form, external_supplier_name: e.target.value })}
              data-testid="maint-supplier-new-name"
              placeholder="e.g. Coastal Diesel Repairs"
            />
          </div>
          <div>
            <div className="text-xs font-medium text-zinc-700 mb-1.5">
              Save this supplier to your network?
            </div>
            <div className="flex gap-2" data-testid="maint-new-supplier-save-choice">
              <button
                type="button"
                onClick={() => setForm({ ...form, _new_supplier_save: true })}
                className={`text-xs rounded-md px-3 py-1.5 border ${
                  form._new_supplier_save === true
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid="maint-new-supplier-yes"
              >
                Yes — save to network
              </button>
              <button
                type="button"
                onClick={() => setForm({ ...form, _new_supplier_save: false })}
                className={`text-xs rounded-md px-3 py-1.5 border ${
                  form._new_supplier_save === false
                    ? "bg-ink text-white border-ink"
                    : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
                }`}
                data-testid="maint-new-supplier-no"
              >
                No — just this job
              </button>
            </div>
            <div className="text-[11px] text-zinc-500 mt-1.5">
              Saving lets you reuse this vendor on future jobs and across yachts.
            </div>
          </div>
          {form._new_supplier_save === true && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2 border-t border-zinc-200">
              <div>
                <div className="text-xs font-medium text-zinc-700 mb-1">Type</div>
                <select
                  className="h-9 w-full rounded-md border border-input px-3 text-sm"
                  value={form._new_supplier_type}
                  onChange={(e) => setForm({ ...form, _new_supplier_type: e.target.value })}
                  data-testid="maint-new-supplier-type"
                >
                  <option value="company">Company</option>
                  <option value="individual">Individual</option>
                </select>
              </div>
              <div>
                <div className="text-xs font-medium text-zinc-700 mb-1">Category</div>
                <select
                  className="h-9 w-full rounded-md border border-input px-3 text-sm"
                  value={form._new_supplier_category}
                  onChange={(e) => setForm({ ...form, _new_supplier_category: e.target.value })}
                  data-testid="maint-new-supplier-category"
                >
                  {SUPPLIER_CATEGORY_KEYS.map((k) => (
                    <option key={k} value={k}>{SUPPLIER_CATEGORY_LABELS[k]}</option>
                  ))}
                </select>
              </div>
              <div>
                <div className="text-xs font-medium text-zinc-700 mb-1">Contact person</div>
                <Input
                  value={form._new_supplier_contact_person}
                  onChange={(e) => setForm({ ...form, _new_supplier_contact_person: e.target.value })}
                  data-testid="maint-new-supplier-contact"
                />
              </div>
              <div>
                <div className="text-xs font-medium text-zinc-700 mb-1">Email *</div>
                <Input
                  type="email"
                  value={form._new_supplier_email}
                  onChange={(e) => setForm({ ...form, _new_supplier_email: e.target.value })}
                  data-testid="maint-new-supplier-email"
                />
              </div>
              <div className="sm:col-span-2">
                <div className="text-xs font-medium text-zinc-700 mb-1">Phone *</div>
                <Input
                  value={form._new_supplier_phone}
                  onChange={(e) => setForm({ ...form, _new_supplier_phone: e.target.value })}
                  data-testid="maint-new-supplier-phone"
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
